/**
 * TrajectoryTranscript — one whole run, delivered by a single server request.
 *
 * The server resolves the run's trace keys from the gateway's D1 index and
 * reads the bodies concurrently (TRA-1089), so the browser never lists or
 * fans out per turn. Both doors (conversation id, full trace key) feed their
 * documents here; rendering is TranscriptPanel so the two can't drift.
 */

import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'

import type { TraceDocument } from '@/api/generated/model'
import { useListTrajectoriesApiTrajectoriesGet } from '@/api/generated/trajectories/trajectories'
import { buildTurns } from '@/components/review/transcript'
import { docToTurn } from '@/components/review/turns'
import { ReviewComposer } from '@/components/review/ReviewComposer'
import { parseVerdictSummary } from '@/components/review/flagSource'
import { useLabels } from '@/hooks/useLabels'
import { useTeam } from '@/contexts/TeamContext'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { downloadConversationExport } from './conversationExport'
import { TranscriptPanel } from './TranscriptPanel'
import { TRANSCRIPT_LOAD_ERROR_MESSAGES, type TranscriptLoadError } from './transcriptLoadError'

const DOWNLOAD_LABEL = 'Download this conversation as JSON'

/**
 * Most recent turns rendered at first, and how many more each press of
 * "Show earlier turns" adds. Every document is already in memory; the cost
 * is assembly, which JSON.parses every request and response body (hundreds
 * of KB per turn on a real agent run), so an unbounded run would freeze
 * the tab. The tail is what people read; the cap is stated in the UI,
 * never silent, and the reader lifts it a page at a time.
 */
const TURNS_PER_PAGE = 100

interface TrajectoryTranscriptProps {
  idLabel: string
  idValue: string
  /** Oldest first, as the server assembled it. Null while loading. */
  docs: readonly TraceDocument[] | null
  /** Null unless the run failed to load; 404 and storage faults read differently. */
  loadError: TranscriptLoadError | null
}

export function TrajectoryTranscript({
  idLabel,
  idValue,
  docs,
  loadError,
}: TrajectoryTranscriptProps): React.JSX.Element {
  const labels = useLabels()
  const metadataKey = idLabel === 'Conversation' ? idValue : docs?.[0]?.traceKey
  const metadata = useListTrajectoriesApiTrajectoriesGet(
    idLabel === 'Conversation'
      ? { range: 'all', conversation_id: idValue }
      : { range: 'all', trace_key: metadataKey },
    { query: { enabled: typeof metadataKey === 'string' && docs !== null } },
  )
  const run = metadata.data?.status === 200 ? metadata.data.data.items[0] : undefined
  const { isRestricted, meLoaded } = useTeam()
  const [renderedCap, setRenderedCap] = useState(TURNS_PER_PAGE)
  const total = docs?.length ?? 0
  const skipped = Math.max(0, total - renderedCap)
  const nextPage = Math.min(TURNS_PER_PAGE, skipped)
  const turns = useMemo(
    () => buildTurns((docs ?? []).slice(skipped).map(docToTurn), skipped),
    [docs, skipped],
  )

  if (loadError !== null) {
    return (
      <p role="alert" className="border border-dashed border-destructive/40 p-6 text-sm text-destructive">
        {TRANSCRIPT_LOAD_ERROR_MESSAGES[loadError]}
      </p>
    )
  }
  if (docs === null) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }
  const first = docs[0]
  if (turns.length === 0 || first === undefined) {
    return <p className="text-sm text-muted-foreground">No traces found for this run.</p>
  }
  return (
    <TranscriptPanel
      turns={turns}
      idLabel={idLabel}
      idValue={idValue}
      totalTurns={total}
      cappedFirstSeenIso={skipped > 0 ? first.timestamp : null}
      run={run}
      actions={
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          title={DOWNLOAD_LABEL}
          aria-label={DOWNLOAD_LABEL}
          // The run attributes feed the file's summary, so wait for them.
          disabled={metadata.isLoading}
          onClick={() => {
            downloadConversationExport(docs, run, {
              idLabel,
              idValue,
              sourceUrl: window.location.href,
              exportedAt: new Date(),
            })
          }}
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </Button>
      }
      renderReview={(turn) => {
        const doc = docs.find((item) => item.traceId === turn.traceId)
        const label = labels.data?.find((item) => item.trace_id === turn.traceId)
        const flagged = label !== undefined && !label.tag_only
        const flagVerdict = label === undefined ? null : label.review_status === 'reviewed'
          ? (label.approved ? 'good' : 'bad') : parseVerdictSummary(label.notes).lean
        const traceKey = doc?.traceKey
        if (traceKey === undefined || traceKey === null) {
          return null
        }
        return (
          <>
            {labels.data !== undefined && (
              <ReviewComposer
                item={{
                  trace_id: turn.traceId,
                  trace_key: traceKey,
                  notes: label === undefined ? '' : label.notes,
                  approved: label?.approved === true,
                  review_status: label === undefined ? 'unreviewed' : label.review_status,
                }}
                appendComment
                flagged={flagged}
                flagVerdict={flagged ? flagVerdict : null}
                readOnly={!meLoaded || isRestricted}
                expectedUpdatedAt={null}
                canSave={meLoaded && !isRestricted && !labels.isLoading && !labels.isError}
                onSaved={() => {
                  /* Shared label mutation invalidates feedback and queue. */
                }}
              />
            )}
          </>
        )
      }}
      banners={
        <>
          {docs.some((doc) => doc.traceKey == null) && <p role="alert" className="text-sm text-destructive">Some rows are missing their trace keys and cannot be reviewed.</p>}
          {metadata.isLoading && <p role="status" className="text-sm text-muted-foreground">Loading run attributes…</p>}
          {metadata.isError && <p role="alert" className="text-sm text-destructive">Failed to load run attributes. Refresh to retry.</p>}
          {labels.isError && (
            <p role="alert" className="text-sm text-destructive">
              Failed to load feedback. Refresh to retry.
            </p>
          )}
          {labels.isLoading && (
            <p role="status" className="text-sm text-muted-foreground">
              Loading feedback…
            </p>
          )}
          {skipped > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-l-2 border-warning bg-card px-3 py-2 text-xs text-muted-foreground">
              <p role="status">
                Showing the most recent {turns.length} of {total} turns.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setRenderedCap((cap) => cap + TURNS_PER_PAGE)
                }}
              >
                Show {nextPage} earlier {nextPage === 1 ? 'turn' : 'turns'}
              </Button>
            </div>
          )}
        </>
      }
    />
  )
}
